import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, History, RotateCcw } from 'lucide-react';

import { ActionButton } from '@/shared/ui/action-button';
import { Slider } from '@/components/ui/slider';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

import { formatWeekDate } from '../lib/time-machine.utils';
import type { UseTimeMachine } from '../model/use-time-machine';

interface TimeMachineControlProps {
  timeMachine: UseTimeMachine;
}

/**
 * A floating card on a desktop map, a bar across the bottom on a phone.
 *
 * The step arrows and the week caption are desktop-only: on a touch screen the slider
 * is the faster control, and the date already says where in history we are. The point
 * of the whole control is watching the map change, so it stays out of the map's way
 * rather than covering it.
 */
export function TimeMachineControl({ timeMachine }: TimeMachineControlProps) {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const { currentWeek, minWeek, maxWeek, setWeek, stepBackward, stepForward, reset } = timeMachine;

  const date = formatWeekDate(currentWeek, i18n.language);

  return (
    <div
      className={cn(
        'absolute z-[1000] rounded-lg bg-white shadow-lg',
        isMobile ? 'inset-x-2 bottom-2 p-2' : '-translate-x-1/2 bottom-4 left-1/2 w-[420px] max-w-[calc(100%-2rem)] p-4'
      )}>
      {!isMobile && (
        <div className="mb-2 flex items-center gap-2">
          <History className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium text-sm">{t('welcome.map.timeline.label')}</span>
          <span className="ml-auto text-muted-foreground text-sm">{date}</span>
        </div>
      )}

      <div className="flex items-center gap-2">
        {isMobile && <span className="shrink-0 text-muted-foreground text-xs">{date}</span>}

        {!isMobile && (
          <ActionButton size="sm" intent="view" onClick={stepBackward} disabled={currentWeek <= minWeek}>
            <ChevronLeft className="h-4 w-4" />
          </ActionButton>
        )}

        <Slider
          className="flex-1"
          value={[currentWeek]}
          min={minWeek}
          max={maxWeek}
          step={1}
          onValueChange={([value]) => setWeek(value)}
        />

        {!isMobile && (
          <ActionButton size="sm" intent="view" onClick={stepForward} disabled={currentWeek >= maxWeek}>
            <ChevronRight className="h-4 w-4" />
          </ActionButton>
        )}

        <ActionButton
          size="sm"
          intent="view"
          onClick={reset}
          disabled={currentWeek === maxWeek}
          aria-label={t('common.reset')}>
          <RotateCcw className="h-4 w-4" />
        </ActionButton>
      </div>

      {!isMobile && (
        <div className="mt-2 text-center text-foreground text-xs">
          {t('gen.week.label')} {currentWeek}
        </div>
      )}
    </div>
  );
}
